import React from 'react';
import PropTypes from 'prop-types';

const PAYMENT_LABELS = {
  creditcard: '💳 บัตรเครดิต',
  promtpay: '📱 พร้อมเพย์',
  cash: '💵 เงินสด',
};

const getCategoryNames = (coupon, categoryList) => {
  if (!coupon || typeof coupon !== 'object') return [];
  const raw = coupon.productcategory_id;
  if (!raw) return [];
  const ids = String(raw)
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  return ids.map((cid) => {
    const found = categoryList.find((c) => (c.productcategory_id || '').trim() === cid);
    return found ? found.productcategory_name : cid;
  });
};

/**
 * 1. Visibility Check: Coupons that do not match the cart's categories or payment methods are hidden completely.
 */
const isCategoryMatch = (coupon, cartCategoryIds) => {
  if (!coupon || typeof coupon !== 'object') return true;
  const couponCatRaw = coupon.productcategory_id;
  if (!couponCatRaw) return true;

  const couponCatIds = String(couponCatRaw)
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

  if (couponCatIds.length === 0) return true;
  if (!cartCategoryIds || cartCategoryIds.length === 0) return false;

  return cartCategoryIds.some((cartCid) => couponCatIds.includes(cartCid));
};

const isPaymentMatch = (coupon, currentPaymentMethod) => {
  if (!coupon || typeof coupon !== 'object') return true;
  const couponPmsRaw = coupon.allowed_payment_methods;
  if (!couponPmsRaw || !currentPaymentMethod) return true;

  const allowedPms = String(couponPmsRaw)
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

  if (allowedPms.length === 0) return true;
  return allowedPms.includes(currentPaymentMethod);
};

/**
 * 2. Usability / Minimum Order Check: If min order is not met, coupon is shown but disabled with clear reason.
 */
const checkMinOrderEligibility = (coupon, cartSubtotal = 0, cartCategorySubtotals = {}) => {
  if (!coupon || typeof coupon !== 'object') {
    return { isEligible: true, disabledReason: '' };
  }

  const minOrder = Number(coupon.min_order) || 0;
  if (minOrder <= 0) {
    return { isEligible: true, disabledReason: '' };
  }

  const couponCatRaw = coupon.productcategory_id;
  if (couponCatRaw) {
    const couponCatIds = String(couponCatRaw)
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);

    if (couponCatIds.length > 0) {
      let catSubtotal = 0;
      couponCatIds.forEach((cid) => {
        catSubtotal += Number(cartCategorySubtotals[cid] || 0);
      });

      if (catSubtotal < minOrder) {
        return {
          isEligible: false,
          disabledReason: `ยอดซื้อสินค้าในหมวดนี้ไม่ถึงขั้นต่ำ ฿${minOrder.toLocaleString()}`,
        };
      }
      return { isEligible: true, disabledReason: '' };
    }
  }

  if (Number(cartSubtotal) < minOrder) {
    return {
      isEligible: false,
      disabledReason: `ยอดสั่งซื้อไม่ถึงขั้นต่ำ ฿${minOrder.toLocaleString()}`,
    };
  }

  return { isEligible: true, disabledReason: '' };
};

const getVoucherMetadata = (coupon, categoryList, availableCoupons = []) => {
  const isObject = typeof coupon === 'object' && coupon !== null;
  const rawCode = isObject ? coupon.coupon_id : String(coupon || '');
  const code = (rawCode || '').trim();

  // Look up full details from availableCoupons if current coupon is only an ID
  const matchedAvail = (availableCoupons || []).find(
    (ac) => (ac?.coupon_id || '').trim().toUpperCase() === code.toUpperCase(),
  );
  const target =
    isObject &&
    (coupon.coupon_type || coupon.discount !== undefined || coupon.cashback_percent !== undefined)
      ? coupon
      : matchedAvail || coupon;
  const isTargetObj = typeof target === 'object' && target !== null;

  const rawType = isTargetObj ? target.coupon_type || 'product' : 'product';
  const type = String(rawType || 'product')
    .trim()
    .toLowerCase();
  const discount =
    isTargetObj && target.discount !== undefined && target.discount !== null
      ? Number(target.discount)
      : null;
  const minOrder =
    isTargetObj && target.min_order !== undefined && target.min_order !== null
      ? Number(target.min_order)
      : null;
  const isFreeShipping = type === 'free_shipping';
  const isCashback = type === 'cashback';
  const cashbackPercent = isTargetObj ? target.cashback_percent : null;
  const maxDiscount = isTargetObj ? target.max_discount : null;
  const isStackable = isTargetObj ? target.is_stackable !== false : true;
  const catNames = getCategoryNames(target, categoryList);
  const allowedPms =
    isTargetObj && target.allowed_payment_methods
      ? String(target.allowed_payment_methods)
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
      : [];

  let typeLabel = 'ส่วนลดสินค้า';
  let iconClass = 'fas fa-tag';
  let cardTypeClass = 'product';

  if (isFreeShipping) {
    typeLabel = 'ส่งฟรี';
    iconClass = 'fas fa-shipping-fast';
    cardTypeClass = 'free_shipping';
  } else if (type === 'shipping') {
    typeLabel = 'ส่วนลดค่าส่ง';
    iconClass = 'fas fa-truck';
    cardTypeClass = 'shipping';
  } else if (isCashback) {
    typeLabel = 'รับ Meya Coins';
    iconClass = 'fas fa-coins';
    cardTypeClass = 'cashback';
  }

  let discTitle = 'โค้ดส่วนลดพิเศษ';
  if (isFreeShipping) {
    discTitle = 'โค้ดส่งฟรีเต็มจำนวน';
  } else if (isCashback) {
    discTitle = `เงินคืน ${cashbackPercent || 0}% Coins`;
  } else if (type === 'shipping' && discount) {
    discTitle = `ลดค่าส่ง ฿${discount.toLocaleString()}`;
  } else if (discount) {
    discTitle = `ลด ฿${discount.toLocaleString()}`;
  }

  return {
    code,
    typeLabel,
    iconClass,
    cardTypeClass,
    discTitle,
    minOrder,
    isCashback,
    maxDiscount,
    isStackable,
    catNames,
    allowedPms,
  };
};

const VoucherCard = ({
  coupon,
  availableCoupons = [],
  categoryList,
  isApplied = false,
  isEligible = true,
  disabledReason = '',
  mode = 'applied',
  onApply,
  onRemove,
}) => {
  const meta = getVoucherMetadata(coupon, categoryList, availableCoupons);

  return (
    <div
      className={`shopee-voucher-card ${meta.cardTypeClass} font-th ${
        mode === 'available' && isApplied ? 'applied-highlight' : ''
      } ${mode === 'available' && !isEligible ? 'ineligible' : ''}`}
    >
      <div className='shopee-voucher-badge'>
        <i className={`${meta.iconClass} shopee-voucher-icon`}></i>
        <span className='shopee-voucher-type-text'>{meta.typeLabel}</span>
      </div>

      <div className='shopee-voucher-content'>
        <div className='shopee-voucher-header'>
          <span className='shopee-voucher-code'>{meta.code}</span>
          <span className='shopee-voucher-disc font-weight-bold'>{meta.discTitle}</span>
        </div>

        <div className='shopee-voucher-terms'>
          {meta.minOrder ? `ขั้นต่ำ ฿${Number(meta.minOrder).toLocaleString()}` : 'ไม่มีขั้นต่ำ'}
          {meta.isCashback && meta.maxDiscount
            ? ` • สูงสุด ฿${Number(meta.maxDiscount).toLocaleString()}`
            : ''}
        </div>

        <div className='shopee-voucher-tags'>
          {meta.isStackable ? (
            <span className='shopee-tag shopee-tag-stack'>🔗 ใช้ร่วมได้</span>
          ) : (
            <span
              className='shopee-tag shopee-tag-stack'
              style={{
                background: '#f5f3ff',
                color: '#6d28d9',
                borderColor: '#ddd6fe',
              }}
            >
              🔒 ใช้เดี่ยว
            </span>
          )}

          {meta.catNames.length > 0 ? (
            <span
              className='shopee-tag'
              style={{
                background: '#fef3c7',
                color: '#b45309',
                borderColor: '#fde68a',
              }}
            >
              🏷️ เฉพาะหมวด: {meta.catNames.join(', ')}
            </span>
          ) : (
            <span className='shopee-tag' style={{ background: '#f1f5f9', color: '#475569' }}>
              ทุกหมวดหมู่
            </span>
          )}

          {meta.allowedPms.length > 0 ? (
            meta.allowedPms.map((pm) => (
              <span key={pm} className='shopee-tag shopee-tag-pm'>
                {PAYMENT_LABELS[pm] || pm}
              </span>
            ))
          ) : (
            <span className='shopee-tag' style={{ background: '#f1f5f9', color: '#475569' }}>
              ทุกช่องทางชำระเงิน
            </span>
          )}
        </div>

        {mode === 'available' && !isEligible && disabledReason && (
          <div className='shopee-ineligible-msg'>
            <i className='fas fa-exclamation-circle'></i> {disabledReason}
          </div>
        )}
      </div>

      <div
        className='shopee-voucher-actions'
        style={mode === 'available' ? { paddingRight: '12px' } : undefined}
      >
        {mode === 'applied' && onRemove && (
          <button
            type='button'
            className='btn-remove-shopee-voucher'
            title='ยกเลิกคูปองนี้'
            onClick={() => onRemove(meta.code)}
          >
            <i className='fas fa-times mr-1'></i> ลบ
          </button>
        )}

        {mode === 'available' &&
          (isApplied ? (
            <button
              type='button'
              className='btn btn-sm btn-outline-success font-th'
              style={{ fontSize: '11px', borderRadius: '6px' }}
              onClick={() => onRemove && onRemove(meta.code)}
            >
              <i className='fas fa-check mr-1'></i> ใช้แล้ว
            </button>
          ) : !isEligible ? (
            <button
              type='button'
              className='btn btn-sm btn-secondary font-th'
              style={{
                fontSize: '11px',
                borderRadius: '6px',
                cursor: 'not-allowed',
                opacity: 0.65,
              }}
              title={disabledReason}
              disabled
            >
              ยอดไม่ถึง
            </button>
          ) : (
            <button
              type='button'
              className='btn btn-sm btn-primary font-th'
              style={{ fontSize: '11px', borderRadius: '6px' }}
              onClick={() => onApply && onApply(meta.code)}
            >
              ใช้โค้ด
            </button>
          ))}
      </div>
    </div>
  );
};

VoucherCard.propTypes = {
  coupon: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
  availableCoupons: PropTypes.array,
  categoryList: PropTypes.array,
  isApplied: PropTypes.bool,
  isEligible: PropTypes.bool,
  disabledReason: PropTypes.string,
  mode: PropTypes.oneOf(['applied', 'available']),
  onApply: PropTypes.func,
  onRemove: PropTypes.func,
};

/**
 * Shopee-style Voucher Picker & Applied Voucher Component
 */
const VoucherPicker = ({
  appliedCoupons = [],
  availableCoupons = [],
  showAvailable = true,
  categoryList = [],
  cartCategoryIds = [],
  cartSubtotal = 0,
  cartCategorySubtotals = {},
  currentPaymentMethod = '',
  onApplyCoupon,
  onRemoveCoupon,
}) => {
  const appliedCodeList = appliedCoupons
    .map((c) => (typeof c === 'object' && c !== null ? c.coupon_id : String(c || '')))
    .map((s) => s.trim().toUpperCase());

  // 1. Only show coupons that match the cart's categories & selected payment method (hide non-matching categories completely)
  const visibleAvailableCoupons = availableCoupons.filter((coupon) => {
    const catMatch = isCategoryMatch(coupon, cartCategoryIds);
    const pmMatch = isPaymentMatch(coupon, currentPaymentMethod);
    return catMatch && pmMatch;
  });

  const hasApplied = appliedCoupons && appliedCoupons.length > 0;
  const hasAvailable =
    showAvailable && visibleAvailableCoupons.length > 0 && Boolean(onApplyCoupon);

  if (!hasApplied && !hasAvailable) return null;

  return (
    <div className='voucher-picker-container'>
      {/* 1. Shopee-style Applied Voucher Tickets */}
      {hasApplied && (
        <div className='applied-vouchers-container mb-3'>
          <div className='d-flex align-items-center justify-content-between mb-2'>
            <span
              className='font-weight-bold font-th'
              style={{ fontSize: '13px', color: '#1e293b' }}
            >
              <i className='fas fa-receipt mr-1 text-success'></i> โค้ดส่วนลดที่ใช้ (
              {appliedCoupons.length} ใบ)
            </span>
          </div>

          <div className='shopee-vouchers-list'>
            {appliedCoupons.map((coupon, idx) => {
              const code =
                typeof coupon === 'object' && coupon !== null
                  ? coupon.coupon_id
                  : String(coupon || '');
              return (
                <VoucherCard
                  key={code || idx}
                  coupon={coupon}
                  availableCoupons={availableCoupons}
                  categoryList={categoryList}
                  mode='applied'
                  onRemove={onRemoveCoupon}
                />
              );
            })}
          </div>
        </div>
      )}

      {/* 2. Available Public / Personal Vouchers Section */}
      {hasAvailable && (
        <div className='available-vouchers-section mt-2'>
          <div className='d-flex align-items-center justify-content-between mb-2'>
            <span
              className='font-weight-bold font-th'
              style={{ fontSize: '13px', color: '#475569' }}
            >
              <i className='fas fa-tags mr-1 text-primary'></i> โค้ดส่วนลดที่สามารถใช้ได้
            </span>
          </div>

          <div className='shopee-vouchers-list' style={{ maxHeight: '280px', overflowY: 'auto' }}>
            {visibleAvailableCoupons.map((coupon) => {
              const code = coupon.coupon_id;
              const isApplied = appliedCodeList.includes((code || '').trim().toUpperCase());
              const { isEligible, disabledReason } = checkMinOrderEligibility(
                coupon,
                cartSubtotal,
                cartCategorySubtotals,
              );

              return (
                <VoucherCard
                  key={code}
                  coupon={coupon}
                  categoryList={categoryList}
                  isApplied={isApplied}
                  isEligible={isEligible}
                  disabledReason={disabledReason}
                  mode='available'
                  onApply={onApplyCoupon}
                  onRemove={onRemoveCoupon}
                />
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

VoucherPicker.propTypes = {
  appliedCoupons: PropTypes.array,
  availableCoupons: PropTypes.array,
  showAvailable: PropTypes.bool,
  categoryList: PropTypes.array,
  cartCategoryIds: PropTypes.array,
  cartSubtotal: PropTypes.number,
  cartCategorySubtotals: PropTypes.object,
  currentPaymentMethod: PropTypes.string,
  onApplyCoupon: PropTypes.func,
  onRemoveCoupon: PropTypes.func,
};

export default React.memo(VoucherPicker);
